import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PlanStep } from '../../../../services/PlanStep';
import { StrtPriority } from '../../../../../shared/models/plan/StrtPriority';
import { StepComponent } from '../../../step.component';
import { PriorityStackSectionPosition } from '../../../../services/PriorityStackSectionPosition';
import { IPriorityStackRow } from '../../../../services/IPriorityStackRow';
import { EditOverlayComponent } from '../../edit-overlay/edit-overlay.component';
import { PlanCoordinate } from '../../../../services/plan-coordinate';
import { APP_CONFIG, AppConfig } from '../../../../../app-config.module';
import * as _ from 'lodash';
import {ResourceService} from '../../../../services/resource.service';

@Component({
  selector: 'app-priority-stack-section',
  templateUrl: './priority-stack-section.component.html',
  styleUrls: ['./priority-stack-section.component.scss']
})
export class PriorityStackSectionComponent extends StepComponent<void> implements OnInit {

  private static MAX_LINE_LENGTH = 70;

  @Input() placeholderColor: string;
  @Input() heading: string;
  @Input() dataKey: string;
  @Input() dataValueKey: string;
  @Input() sectionPosition: PriorityStackSectionPosition;
  @Input() targetCoordinate: PlanCoordinate;
  @Input() pageIndex: number;
  @Input() hasFullPriorityList: boolean;
  /**
   * Property path to use for deduplication
   * If no value is provided, there will be no deduplication
   */
  @Input() dedupeByKey: string;
  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();
  @ViewChild(EditOverlayComponent) editOverlayComp: EditOverlayComponent;
  private actualMaxDataRowCount = 0;
  public rowData: IPriorityStackRow;

  constructor(@Inject(APP_CONFIG) private appConfig: AppConfig, _resourceService: ResourceService) {
    super(PlanCoordinate.stepBy(PlanStep.OUTPUTS), _resourceService);
  }

  ngOnInit() {}

  @Input() set priorities(priorities: StrtPriority[]) {
    // extract necessary data elements
    this.rowData = {
      sections : []
    };
    this.actualMaxDataRowCount = 1; // defaults to 1 to account for other columns having 0 data rows
    if (priorities) {
      for (const priority of priorities) {
        const columnData = {
          dataRows: this.aggregateDataValues(priority, this.dataKey, this.dataValueKey, this.dedupeByKey) || [],
          colorCode: priority.colorCode
        };
        if (columnData.dataRows.length > this.actualMaxDataRowCount) {
          this.actualMaxDataRowCount = columnData.dataRows.length;
        }
        this.rowData.sections.push(columnData);
      }
    }
  }

  get placeholderDataRows() {
    const output = [];
    for (let i = 0; i < this.actualMaxDataRowCount; i++) {
      output.push({});
    }
    return output;
  }

  get sectionContentClass() {
    switch (this.sectionPosition) {
      case PriorityStackSectionPosition.Top:
        return 'priority-stack-section-content top';
      case PriorityStackSectionPosition.Middle:
        return 'priority-stack-section-content middle';
      case PriorityStackSectionPosition.Bottom:
        return 'priority-stack-section-content bottom';
    }
  }

  get sectionClass() {
    switch (this.sectionPosition) {
      case PriorityStackSectionPosition.Top:
        return 'priority-stack-section top';
      case PriorityStackSectionPosition.Middle:
        return 'priority-stack-section middle';
      case PriorityStackSectionPosition.Bottom:
        return 'priority-stack-section bottom';
    }
  }

  /**
   * Recursively builds data array from nested objects with a given data key and data value key
   *
   * @example aggregateDataValue(priorityX, 'objectives', 'text')
   *
   *            This would look for an 'objectives' property in the priorityX object
   *            If that property exist then the function would iterate through items in the array
   *              and extract values using the dataValueKey and push the results into the output array
   *
   * @example aggregateDataValue(priorityX, 'objectives.initiatives', 'description')
   *
   *            This example contains a multipart dot concatenated data key
   *            First this function would split the multipart key and use the first part to
   *              extract the first nested array
   *            Then the function would use the first nested array as the input object to a
   *              recursive call back to self, passing in an updated dataKey while maintaining
   *              the same dataValueKey
   *              - 1st recursive call is effectively the same as:
   *              -- aggregateDataValue(priorityX.objectives, 'initiatives', 'description')
   *            Inside the recursive execution the dataKey would no longer be a multipart key and
   *              data extraction would proceed as normal
   *
   * @example aggregateDataValue(priorityX, 'objectives.initiatives.keyMeasures', 'measure')
   *
   *            This example contains a multipart dot concatenated data key
   *            First this function would split the multipart key and use the first part to
   *              extract the first nested array
   *            Then the function would use the first nested array as the input object to a
   *              recursive call back to self, passing in an updated dataKey while maintaining
   *              the same dataValueKey
   *              - 1st recursive call is effectively the same as:
   *              -- aggregateDataValue(priorityX.objectives, 'initiatives.keyMeasures', 'measure')
   *            Inside the recursive execution the dataKey would still be a multipart key and the
   *              recursion would happen again until the final key is reached
   *              - 2nd recursive call is effectively the same as:
   *              -- aggregateDataValue(priorityX.objectives.initiatives, 'keyMeasures', 'measure')
   *
   * @param {{}} inObj - The object that needs to be traversed
   * @param {string} dataKey - The location key for the values (Multipart keys are dot concatenated)
   * @param {string} dataValueKey - The value key (Used to extract data values used in output object)
   * @returns {string[]}
   */
  aggregateDataValues(inObj: {}, dataKey: string, dataValueKey: string, dedupeKey?: string) {
    if (!dataKey || !dataValueKey) {
      return;
    }
    // If the dataKey is a multipart split it into pieces, else assign a string array with just the final dataKey
    const keySplit = dataKey.indexOf('.') >= 0 ? dataKey.split('.') : [dataKey];
    const outputData: string[] = [];
    if (keySplit) {
      // Extract the first key from the post split dataKey
      const lvlKey = keySplit[0];
      // If this is not a multipart then check for the existance of the dataKey as a property of the input object
      if (keySplit.length === 1 && inObj.hasOwnProperty(lvlKey)) {
        // The input object does have a property matching that dataKey, start iterating through nested objects and extract
        // the data that is linked to the dataValueKey
        for (const objX of inObj[lvlKey]) {
          if (objX.hasOwnProperty(dataValueKey)) {
            outputData.push(objX[dataValueKey]);
          }
        }
      } else if (keySplit.length > 1 && inObj.hasOwnProperty(lvlKey)) {
        // ^^ If this is a multipart key then check for the existance of the dataKey as a property of the input object
        // The input object does have a property matching the first part of the multipart dataKey
        // Create a new multipart dataKey that excludes the first level
        const subLvlKey = keySplit.slice(1, keySplit.length).join('.');
        // Extract the data attached to the first level key
        const subLvlArrData = inObj[lvlKey];
        const subLvlCmbData = {};
        subLvlCmbData[keySplit[1]] = [];
        // Reassemble an input object from the extracted first level data to be used in the next recursive execution
        for (const obj of subLvlArrData) {
          subLvlCmbData[keySplit[1]].push(...obj[keySplit[1]]);
        }
        // Recursively call self with parameters that would go one level deeper in the object tree and push
        // results into an output array
        const subValues = this.aggregateDataValues(subLvlCmbData, subLvlKey, dataValueKey, dedupeKey);
        outputData.push(...subValues);
      }
    }
    return _.uniqBy(outputData, function(e) {
      return e;
    });
  }

  get needsPlaceholderColumn() {
    return (!this.hasFullPriorityList) &&
      this.rowData.sections.length < this.appConfig.uiRules.deliverables.strategyMap.MAX_COLUMNS_PER_PAGE;
  }

  get needsFillerPlaceholderColumns() {
    return this.rowData.sections.length === 0 && this.pageIndex === 0;
  }

  get needsInvisiColumn() {
    return (this.rowData.sections.length > 0 || this.pageIndex > 0)
      && this.rowData.sections.length < this.appConfig.uiRules.deliverables.strategyMap.MAX_COLUMNS_PER_PAGE - 1;
  }

  get needsSecondInvisiColumn() {
    return ((this.rowData.sections.length > 0 || this.pageIndex > 0)
      && (this.rowData.sections.length === 0 || (this.rowData.sections.length === 1 && this.hasFullPriorityList)));
  }

  get transparent() {
    return 'transparent';
  }

  get envisioningStep() {
    return PlanStep.SYNTHESIZE_RESULTS;
  }

  public onClickPurposeMissionBeliefs() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.SYNTHESIZE_RESULTS));
  }

  public onPlanCoordinateChange(planCoordinate: PlanCoordinate) {
    this.planCoordinateChange.emit(planCoordinate);
  }

  get firstColumn() {
    if (this.hasFirstColumn) {
      return this.rowData.sections[0];
    } else {
      return [];
    }
  }

  get hasFirstColumn() {
    return this.rowData.sections.length > 0;
  }

  get secondColumn() {
    if (this.hasSecondColumn) {
      return this.rowData.sections[1];
    } else {
      return [];
    }
  }

  get hasSecondColumn() {
    return this.rowData.sections.length > 1;
  }

  get thirdColumn() {
    if (this.hasThirdColumn) {
      return this.rowData.sections[2];
    } else {
      return [];
    }
  }

  get hasThirdColumn() {
    return this.rowData.sections.length > 2;
  }

  truncateContent(source: string) {
    if (source.length >= PriorityStackSectionComponent.MAX_LINE_LENGTH) {
      return source.slice(0, PriorityStackSectionComponent.MAX_LINE_LENGTH - 3).concat('...');
    } else {
      return source;
    }
  }

}
